import { styled } from "@linaria/react";
import React from "react";
import { DownloadLine, InformationLine } from "@ui/IconV2/react";
import Text from "@ui/Text";
import { colors, spacings } from "@ui/Theme";
import type { Option as InfoTooltipOption } from "../../../Project/Chart/InfoTooltip";
import TooltipV2 from "@ui/TooltipV2";
import { FullViewportTrigger } from "@ui/FullViewportButton";
import { ExportWidgetButton } from "@ui/ExportWidgetButton";
import { generateExportFileName } from "diagnostics-lib/string";
import ButtonIconV2 from "@ui/ButtonIconV2";
import t from "../../../../i18n";
import { css } from "@linaria/core";

export interface ReportingChartTitleProps {
  title: React.ReactNode;
  exportTitle?: React.ReactNode;
  description?: { title: string; description?: string; options: InfoTooltipOption[] };
  matrixSelector?: React.ReactNode;
  dateSelector?: React.ReactNode;
}

const TooltipContent = styled.div`
  max-width: 320px;
  h6 {
    margin-top: 10px;
  }
`;

const TooltipIconContainer = styled.div`
  height: 20px;
  /* Note : Problem aligning the svg with the title when it should be aligned with its container. Problem localized at h4 font level. */
  > svg {
    display: block;
  }
`;

const exportButton = css`
  box-sizing: border-box;
  width: ${spacings.xl};
  height: ${spacings.xl};
  @media print {
    display: none;
  }
  > * {
    pointer-events: none;
  }
`;

function ReportingChartTitle({
  title,
  matrixSelector,
  dateSelector,
  description,
  exportTitle,
}: ReportingChartTitleProps) {
  return (
    <>
      {title}
      {description && (
        <TooltipV2
          title={description.title}
          placement="bottom"
          overlay={
            <TooltipContent>
              <Text as="h5" type="body1SemiBold">
                {description.title}
              </Text>
              <Text type="body2">{description.description}</Text>
              {description.options.map(({ label, description }) => (
                <>
                  <Text as="h6" type="body2SemiBold">
                    {label}
                  </Text>
                  <Text type="body2">{description}</Text>
                </>
              ))}
            </TooltipContent>
          }
        >
          <TooltipIconContainer>
            <InformationLine size="m" color={colors.carbon800} />
          </TooltipIconContainer>
        </TooltipV2>
      )}
      {matrixSelector}
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: spacings.xs }}>
        {dateSelector}
        <ExportWidgetButton
          fileName={generateExportFileName({
            filename: exportTitle?.toString() ?? "",
            projectName: "",
            extension: "png",
          })}
        />
        <FullViewportTrigger />
      </div>
    </>
  );
}

export default React.memo(ReportingChartTitle);

function ReportingMatrixTitle({
  title,
  matrixSelector,
  dateSelector,
  description,
  handleExport,
}: {
  title: string;
  matrixSelector?: React.ReactNode;
  dateSelector?: React.ReactNode;
  description?: { title: string; description?: string; options: InfoTooltipOption[] };
  handleExport: () => void;
}) {
  return (
    <>
      {title}
      {description && (
        <TooltipV2
          title={description.title}
          placement="bottom"
          overlay={
            <TooltipContent>
              <Text as="h5" type="body1SemiBold">
                {description.title}
              </Text>
              <Text type="body2">{description.description}</Text>
              {description.options.map(({ label, description }) => (
                <>
                  <Text as="h6" type="body2SemiBold">
                    {label}
                  </Text>
                  <Text type="body2">{description}</Text>
                </>
              ))}
            </TooltipContent>
          }
        >
          <TooltipIconContainer>
            <InformationLine size="m" color={colors.carbon800} />
          </TooltipIconContainer>
        </TooltipV2>
      )}
      {matrixSelector}
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: spacings.xs }}>
        {dateSelector}
        <TooltipV2 overlay={t("reporting.export.matrix")}>
          <ButtonIconV2 onClick={handleExport} className={exportButton}>
            <DownloadLine size="m" />
          </ButtonIconV2>
        </TooltipV2>
      </div>
    </>
  );
}

export const MemoizedReportingMatrixTitle = React.memo(ReportingMatrixTitle);
